import type { ComputedRef } from 'vue'
import type { SaferGamblingQas } from '@content-utils/schemas'
import { mapSaferGamblingQas, saferGamblingFaqItems, type SaferGamblingQaItem } from '@content-base/utils/saferGamblingQas'
import { computed, useAsyncData, useFaqSchema, useLayeredContent, useRoute, useSiteUrl } from '#imports'

export interface UseSaferGamblingQasOptions {
    key: string
    category: string
}

export interface UseSaferGamblingQasReturn {
    qas: ComputedRef<SaferGamblingQaItem[]>
    /** True while a client-side navigation still loads the Q&As; never after SSR or hydration. */
    isLoading: ComputedRef<boolean>
}

/**
 * Fetches a category's Q&As on the server and adds its questions to the page's FAQPage schema.
 * Await it at the top level of `<script setup>`: the server waits for the data, while a client-side
 * navigation shows the page at once and gets the Q&As after mount, with `isLoading` on meanwhile.
 */
export function useSaferGamblingQas({ key, category }: UseSaferGamblingQasOptions): Promise<UseSaferGamblingQasReturn> {
    const { queryByCategory } = useLayeredContent()
    const { addFaqPage } = useFaqSchema()
    const route = useRoute()
    const siteUrl = useSiteUrl()

    // The payload carries the mapped items, not the raw records with the fields the page never reads.
    const asyncData = useAsyncData(key, () => queryByCategory<SaferGamblingQas>('safer-gambling/qas', category), {
        lazy: true,
        transform: mapSaferGamblingQas,
    })
    const qas = computed(() => asyncData.data.value ?? [])
    const isLoading = computed(() => asyncData.status.value === 'idle' || asyncData.status.value === 'pending')

    return asyncData.then(() => {
        // The schema is only emitted during SSR; rendering every answer on the client would be wasted work.
        if (import.meta.server) addFaqPage(saferGamblingFaqItems(qas.value, { siteUrl: siteUrl(''), pagePath: route.path }))

        return { qas, isLoading }
    })
}
